import { ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export interface FilterOption {
  value: string;
  label: string;
}

/**
 * Filtre à choix multiple d'une liste (comme FilterSelect, plusieurs valeurs) : un bouton qui résume la sélection
 * (« Toutes les équipes », le libellé d'une seule valeur, ou « 2 équipes ») et ouvre des cases à cocher. Aucune case
 * cochée : pas de filtre.
 */
export function MultiFilterSelect({
  label,
  allLabel,
  noun,
  options,
  value,
  onChange,
}: {
  /** Intitulé pour les lecteurs d'écran (« Filtrer par équipe ») */
  label: string;
  /** Sans sélection (« Toutes les équipes ») */
  allLabel: string;
  /** Nom des valeurs au pluriel, pour le résumé (« équipes ») */
  noun: string;
  options: FilterOption[];
  value: string[];
  onChange: (value: string[]) => void;
}) {
  const selected = options.filter((option) => value.includes(option.value));
  const summary =
    selected.length === 0 ? allLabel : selected.length === 1 ? selected[0].label : `${selected.length} ${noun}`;
  const toggle = (option: string) =>
    onChange(value.includes(option) ? value.filter((item) => item !== option) : [...value, option]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${label} : ${summary}`}
          className="flex items-center justify-between gap-2 pl-3 pr-2 py-2 border border-slate-300 rounded-lg text-sm bg-card text-slate-700 min-w-44"
        >
          <span className="truncate">{summary}</span>
          <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2" aria-label={label}>
        <fieldset>
          <legend className="sr-only">{label}</legend>
          {options.map((option) => (
            <label
              key={option.value}
              className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-slate-700 hover:bg-slate-50 cursor-pointer"
            >
              <input
                type="checkbox"
                className="accent-blue-600"
                checked={value.includes(option.value)}
                onChange={() => toggle(option.value)}
              />
              {option.label}
            </label>
          ))}
        </fieldset>
        <button
          type="button"
          disabled={value.length === 0}
          onClick={() => onChange([])}
          className="mt-1 w-full px-2 py-1.5 rounded-md text-sm text-left text-blue-700 hover:bg-blue-50 disabled:text-slate-400 disabled:hover:bg-transparent"
        >
          {allLabel}
        </button>
      </PopoverContent>
    </Popover>
  );
}
